import { CalendarDate, Time } from '@internationalized/date';

import type { DiscountFormResponse } from '../api/useGetDiscount';
import type { DiscountFormState } from '../atoms/discountFormAtoms';
import type { DiscountType } from '../components/types';

/**
 * Converts a Unix ms timestamp to DateValue + TimeValue for date picker components.
 * This is pure UI serialization — the business logic mapping is done server-side.
 */
export const parseTimestamp = (ms: number): { date: CalendarDate | null; time: Time | null } => {
	if (!ms || ms <= 0) return { date: null, time: null };
	const d = new Date(ms);
	return {
		date: new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate()),
		time: new Time(d.getHours(), d.getMinutes()),
	};
};

/** Maps the backend form-friendly response to form atom state. Only converts dates. */
export const mapResponseToFormState = (response: DiscountFormResponse): DiscountFormState => {
	const { date: startDate, time: startTime } = parseTimestamp(response.startsAt);
	const { date: endDate, time: endTime } = response.hasEndDate
		? parseTimestamp(response.endsAt)
		: { date: null, time: null };

	return {
		discountType: response.discountType as DiscountType,
		method: response.method,
		code: response.code,
		title: response.name,
		amountType: response.amountType,
		amount: String(response.amount),
		appliesTo: response.appliesTo,
		applicableProducts: response.applicableProducts,
		startDate,
		startTime,
		hasEndDate: response.hasEndDate,
		endDate,
		endTime,
	};
};
